<script setup>
/**
 * The week, drawn.
 *
 * Seven day columns, hours down the side, a session placed by when it starts
 * and sized by how long it runs. All the arithmetic lives in
 * `helpers/calendarWeek.js` so it can be tested with numbers rather than
 * inferred from a rendered page — a chip half an hour adrift is the kind of
 * fault that reads as "the calendar is broken".
 *
 * The grid asks for a week and is given one: the page fetches by window, so
 * moving to the next week is a request rather than a filter over everything.
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import XlButton from '../xl/XlButton.vue';
import IconChevronRight from '../icons/IconChevronRight.vue';
import IconRepeat from '../icons/IconRepeat.vue';
import { kindOf, SESSION_KINDS } from '../../helpers/sessionKinds';
import { parseSessionDate, timeLabel } from '../../helpers/sessionCard';
import { compactDate } from '../../helpers/usDate';
import {
  DAY_LABELS,
  HOUR_HEIGHT,
  columnFor,
  eventGeometry,
  hourRange,
  hoursIn,
  isSameDay,
  nowOffset,
  weekDays,
  weekLabel,
} from '../../helpers/calendarWeek';

const props = defineProps({
  sessions: { type: Array, default: () => [] },
  /** Which chip the popover is currently describing, if any. */
  openId: { type: [Number, String, null], default: null },
  /** Any date inside the week being shown. */
  anchor: { type: Date, required: true },
  loading: { type: Boolean, default: false },
  /** The week could not be fetched: an empty grid then says nothing about the week. */
  error: { type: Boolean, default: false },
  /** A policyholder's week (XN-4558): nothing to create, so empty hours are plain cells, and the legend says what each colour means to them. */
  readonly: { type: Boolean, default: false },
});

const emit = defineEmits(['move', 'today', 'open', 'new-slot']);

const days = computed(() => weekDays(props.anchor));
/**
 * What the week holds: never a cancelled date. It is not happening, so it
 * takes no slot (Sessions calendar → "a cancelled session is still on the
 * calendar"); the Sessions list is where the hub sees what was cancelled.
 */
const shown = computed(() => props.sessions.filter((session) => session.status !== 'cancelled'));
const range = computed(() => hourRange(shown.value));
const hours = computed(() => hoursIn(range.value));

/**
 * The now-line moves without anything being clicked, so it is kept on a tick
 * of its own rather than being read once at render and left to go stale on a
 * page somebody leaves open all morning.
 */
const now = ref(new Date());
let clock = null;

onMounted(() => {
  clock = setInterval(() => { now.value = new Date(); }, 60_000);
});

onBeforeUnmount(() => {
  if (clock) clearInterval(clock);
});

const todayColumn = computed(() => days.value.findIndex((day) => isSameDay(day, now.value)));
const nowTop = computed(() => nowOffset(range.value, now.value));

const inColumn = (index) => shown.value
  .map((session) => ({ session, geometry: eventGeometry(session, range.value) }))
  .filter(({ session, geometry }) => geometry && columnFor(session, days.value) === index);

// Purple for a session, green for Office Hours. "On duty" is the same
// moderator column relabelled, so the colour can only come from the kind.
const toneOf = (session) => {
  const kind = kindOf(session);

  if (kind === SESSION_KINDS.OFFICE_HOURS) return 'office-hours';

  return 'session';
};

// The chip sits in a column, but a screen reader hears only the button.
const dayName = (session) => compactDate(parseSessionDate(session.start_date), { long: true });
const dayLabel = (index) => DAY_LABELS[index];
const gridStyle = computed(() => ({ '--hour-height': `${HOUR_HEIGHT}px` }));
</script>

<template>
  <section class="calendar" data-testid="session-calendar">
    <header class="calendar__head">
      <h3 class="calendar__week" data-testid="calendar-week">{{ weekLabel(props.anchor) }}</h3>

      <span class="calendar__spacer" />

      <!-- The same chevron the rows use, turned around for "back" rather than
           a different glyph at a different weight. -->
      <XlButton variant="ghost" size="sm" data-testid="calendar-prev" @click="emit('move', -1)">
        <span class="calendar__back" aria-hidden="true"><IconChevronRight color="currentColor" /></span>
        <span class="calendar__sr">Previous week</span>
      </XlButton>
      <XlButton variant="ghost" size="sm" data-testid="calendar-today" @click="emit('today')">Today</XlButton>
      <XlButton variant="ghost" size="sm" data-testid="calendar-next" @click="emit('move', 1)">
        <span aria-hidden="true"><IconChevronRight color="currentColor" /></span>
        <span class="calendar__sr">Next week</span>
      </XlButton>
    </header>

    <p v-if="props.readonly && !props.loading && !props.error && !shown.length" class="calendar__empty">Nothing on this week.</p>

    <div class="calendar__grid" :class="{ 'calendar__grid--loading': props.loading }" :style="gridStyle" :aria-busy="props.loading ? 'true' : undefined">
      <div class="calendar__columns calendar__columns--head">
        <div class="calendar__gutter" />
        <div
          v-for="(day, index) in days"
          :key="day.toISOString()"
          class="calendar__day"
          :class="{ 'calendar__day--today': index === todayColumn }"
          :data-testid="`calendar-day-${index}`"
        >
          <span class="calendar__day-name">{{ dayLabel(index) }}</span>
          <span class="calendar__day-number">{{ day.getDate() }}</span>
        </div>
      </div>

      <div class="calendar__columns calendar__columns--body">
        <div class="calendar__gutter">
          <div v-for="hour in hours" :key="hour" class="calendar__hour">
            <span>{{ hour % 12 === 0 ? 12 : hour % 12 }} {{ hour < 12 ? 'AM' : 'PM' }}</span>
          </div>
        </div>

        <div
          v-for="(day, index) in days"
          :key="day.toISOString()"
          class="calendar__column"
          :class="{ 'calendar__column--today': index === todayColumn }"
        >
          <template v-if="props.readonly">
            <div v-for="hour in hours" :key="hour" class="calendar__cell" />
          </template>
          <template v-else>
            <!-- Empty space is a way to start a session, so it is a button
                 rather than a div that happens to listen for clicks. -->
            <button
              v-for="hour in hours"
              :key="hour"
              type="button"
              class="calendar__cell"
              :data-testid="`calendar-slot-${index}-${hour}`"
              :aria-label="`New session on ${dayLabel(index)} ${day.getDate()} at ${hour}:00`"
              @click="emit('new-slot', { day, hour, event: $event })"
            />
          </template>

          <div
            v-if="index === todayColumn && nowTop !== null"
            class="calendar__now"
            :style="{ top: `${nowTop}px` }"
            data-testid="calendar-now"
            aria-hidden="true"
          ><i /></div>

          <button
            v-for="{ session, geometry } in inColumn(index)"
            :key="session.id"
            type="button"
            class="calendar__event"
            :class="[`calendar__event--${toneOf(session)}`, { 'calendar__event--open': props.openId === session.id}]"
            :style="{ top: `${geometry.top}px`, height: `${geometry.height}px` }"
            :data-testid="`calendar-event-${session.id}`"
            @click.stop="emit('open', { session, event: $event })"
          >
            <b>
              <!-- Each date of a repeating session is its own chip, marked as
                   one of a series. The staff feed says `series_id`; the
                   members' says `series`. -->
              <span
                v-if="session.series_id || session.series"
                class="calendar__repeat"
                :data-testid="`calendar-repeat-${session.id}`"
              ><IconRepeat color="currentColor" size="11" /><span class="calendar__sr">Repeating · </span></span>{{ session.title }}
            </b>
            <span>{{ timeLabel(session) }}</span>
            <span class="calendar__sr">{{ dayName(session) }}</span>
          </button>
        </div>
      </div>
    </div>

    <footer class="calendar__key">
      <template v-if="props.readonly">
        <span><i class="calendar__swatch calendar__swatch--office-hours" aria-hidden="true" /> Office Hours — drop in, no booking</span>
        <span><i class="calendar__swatch calendar__swatch--session" aria-hidden="true" /> Sessions you can join</span>
      </template>
      <template v-else>
        <span><i class="calendar__swatch calendar__swatch--session" aria-hidden="true" /> Session</span>
        <span><i class="calendar__swatch calendar__swatch--office-hours" aria-hidden="true" /> Office Hours</span>
      </template>
    </footer>
  </section>
</template>

<style scoped>
.calendar {
  font-family: var(--xl-font-family);
}

.calendar__head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
}

.calendar__week {
  margin: 0;
  font-size: 17px;
  font-weight: var(--xl-font-weight-bold);
  color: var(--xl-color-on-surface);
}

.calendar__spacer { flex: 1; }

.calendar__back { display: inline-flex; transform: scaleX(-1); }

/* The arrows carry no words, so the direction is said here for anybody who
   cannot see which way the chevron points. */
.calendar__repeat {
  display: inline-flex;
  vertical-align: -1px;
  margin-right: 4px;
  opacity: 0.85;
}

.calendar__sr {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

.calendar__empty {
  margin: 0 0 10px;
  font-size: 14px;
  color: var(--xl-color-on-surface-variant);
}

.calendar__grid {
  border: 1px solid var(--xl-color-border);
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
}

.calendar__columns {
  display: grid;
  grid-template-columns: 56px repeat(7, 1fr);
}

.calendar__columns--head {
  border-bottom: 1px solid var(--xl-color-border);
  background: var(--xl-color-background);
}

/* Every rule INSIDE the grid is the lighter of the two lines: the darker one
   draws the frame and the head's underline, and using it throughout turns a
   week into graph paper. */
.calendar__grid {
  --calendar-rule: var(--xl-color-border-subtle, var(--xl-color-border));
}

.calendar__grid--loading {
  opacity: .55;
  transition: opacity .15s;
}

/* The grid scrolls inside itself so the hours stay reachable without the page
   growing to eleven hours tall. */
.calendar__columns--body {
  position: relative;
  max-height: 560px;
  overflow-y: auto;

  /* How far an hour label reaches above its own row. Declared here because
     this box is what clips it: the label straddles the line at the top of its
     hour, so the first one sits above the content box and `overflow-y` cut it
     in half — 9 AM arrived already half gone. The padding reserves exactly the
     overhang, and both numbers read from this one so they cannot drift apart.

     Padding rather than nudging the first label: the gutter and the day
     columns are siblings in this box, so it moves them together and the hours
     stay level with the rows they name. */
  --hour-label-rise: 7px;
  padding-top: var(--hour-label-rise);
}

.calendar__gutter {
  border-right: 1px solid var(--calendar-rule);
}

.calendar__day {
  border-left: 1px solid var(--calendar-rule);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 9px 4px;
}

.calendar__day-name {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--xl-color-outline);
}

.calendar__day-number {
  font-size: 17px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: var(--xl-color-on-surface);
}

.calendar__day--today .calendar__day-name,
.calendar__day--today .calendar__day-number {
  color: var(--xl-color-primary);
}

.calendar__day--today .calendar__day-number {
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: var(--xl-color-primary);
  color: #fff;
}

.calendar__hour {
  position: relative;
  height: var(--hour-height);
  font-size: 10.5px;
  color: var(--xl-color-outline);
}

/* The label names the line at the top of its hour, so it straddles it. */
.calendar__hour span {
  position: absolute;
  top: calc(var(--hour-label-rise, 7px) * -1);
  right: 7px;
}

.calendar__column {
  position: relative;
  border-left: 1px solid var(--calendar-rule);
}

.calendar__column--today {
  background: color-mix(in srgb, var(--xl-color-primary) 3.5%, transparent);
}

.calendar__cell {
  display: block;
  width: 100%;
  height: var(--hour-height);
  padding: 0;
  border: 0;
  border-bottom: 1px solid var(--calendar-rule);
  background: none;
}

button.calendar__cell {
  cursor: pointer;
}

button.calendar__cell:hover {
  background: var(--xl-color-primary-container);
}

button.calendar__cell:focus-visible {
  outline: 2px solid var(--xl-color-primary);
  outline-offset: -2px;
}

.calendar__now {
  position: absolute;
  left: 0;
  right: 0;
  height: 2px;
  background: var(--xl-color-error);
  z-index: 3;
}

/* The dot is what makes the line read as a clock hand rather than a border. */
.calendar__now i {
  position: absolute;
  left: -4px;
  top: -3px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--xl-color-error);
}

.calendar__event {
  position: absolute;
  left: 3px;
  right: 3px;
  display: flex;
  flex-direction: column;
  /* A half-hour chip has room for one line, and it has to be the title.
     Unwrapped, flex squeezed the title (overflow: hidden lets it shrink to
     nothing) to keep the time. Wrapped, a time that does not fit whole moves
     to a second column, and the column gap puts that a chip's width away,
     past the right padding the clip would otherwise show it through. */
  flex-wrap: wrap;
  gap: 1px 100%;
  padding: 3px 6px;
  border: 0;
  border-radius: 7px;
  overflow: hidden;
  text-align: left;
  font-family: inherit;
  font-size: 11px;
  line-height: 1.3;
  color: #fff;
  cursor: pointer;
  transition: filter 120ms ease;
  z-index: 2;
}

.calendar__event:hover {
  filter: brightness(1.12);
}

@media (prefers-reduced-motion: reduce) {
  .calendar__event { transition: none; }
}

/* One line, cut off — a title that wrapped would push its own time out of a
   chip sized by the clock rather than by the words. */
.calendar__event b {
  /* The full width, so a wrapped time starts past the edge rather than beside it. */
  width: 100%;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.calendar__event span {
  font-size: 10px;
  opacity: 0.8;
}

.calendar__event:focus-visible,
.calendar__event--open {
  outline: 2px solid var(--xl-color-on-surface);
  outline-offset: 1px;
}


.calendar__event--session { background: var(--xl-color-primary); }
/* The same green the Overview's Office Hours dot uses. */
.calendar__event--office-hours { background: var(--xl-color-success, #166b48); }

.calendar__key {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 14px;
  font-size: 12.5px;
  color: var(--xl-color-outline);
}

.calendar__key span {
  display: flex;
  align-items: center;
  gap: 6px;
}

.calendar__swatch {
  width: 11px;
  height: 11px;
  border-radius: 3px;
  display: inline-block;
}

.calendar__swatch--session { background: var(--xl-color-primary); }
.calendar__swatch--office-hours { background: var(--xl-color-success, #166b48); }
</style>
